import { useState } from 'react';
import { Stack } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { ReportAggregatesFilters, ReportOperationsFilters } from '@/api/models';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { PageTitle } from '@/components/shell/PageTitle';
import { PillTabs } from '@/components/shell/PillTabs';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { countActiveAggregatesFilters, countActiveOperationsFilters } from './filters/activeFilterCount';
import { AggregatesFiltersForm } from './filters/AggregatesFiltersForm';
import { OperationsFiltersForm } from './filters/OperationsFiltersForm';
import { AggregatesView } from './AggregatesView';
import { OperationsListView } from './OperationsListView';

const DEFAULT_OPERATIONS_FILTERS: ReportOperationsFilters = { page: 1, pageSize: 50, sort: '-createdAt' };
const DEFAULT_AGGREGATES_FILTERS: ReportAggregatesFilters = {};
const PAGINATION_ONLY_KEYS = new Set(['page', 'pageSize']);

function downloadCsvFilename(): string {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  return `operaciones-${stamp}.csv`;
}

/**
 * Operations surface: a list view and an aggregates view over `/v1/admin/reports/*`,
 * switched with `PillTabs` under one sticky `SectionHeader`/`FiltersPopover`
 * (foundations.md §7.14). CSV export downloads the file the API returns —
 * never built client-side — carrying the list view's currently applied
 * filters.
 */
export function OperationsPage() {
  const translate = useTranslate();
  const notify = useNotify();
  const [view, setView] = useState<'list' | 'aggregates'>('list');
  const [operationsFilters, setOperationsFilters] = useState<ReportOperationsFilters>(DEFAULT_OPERATIONS_FILTERS);
  const [aggregatesFilters, setAggregatesFilters] = useState<ReportAggregatesFilters>(DEFAULT_AGGREGATES_FILTERS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [exporting, setExporting] = useState(false);

  function updateOperationsFilters(patch: Partial<ReportOperationsFilters>) {
    const isPaginationOnly = Object.keys(patch).every((key) => PAGINATION_ONLY_KEYS.has(key));
    setOperationsFilters((prev) => ({ ...prev, ...patch, page: isPaginationOnly ? (patch.page ?? prev.page) : 1 }));
  }

  function updateAggregatesFilters(patch: Partial<ReportAggregatesFilters>) {
    setAggregatesFilters((prev) => ({ ...prev, ...patch }));
  }

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await payApiClient.exportReportOperations(operationsFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadCsvFilename();
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify(translate('pay.operations.list.exportStarted'), { type: 'info' });
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : translate('pay.operations.list.exportError');
      notify(message, { type: 'error' });
    } finally {
      setExporting(false);
    }
  }

  const activeCount =
    view === 'list' ? countActiveOperationsFilters(operationsFilters) : countActiveAggregatesFilters(aggregatesFilters);

  return (
    <Stack spacing={1.5}>
      <PageTitle title={translate('pay.operations.pageTitle')} subtitle={translate('pay.operations.pageSubtitle')} />
      <SectionHeader
        left={
          <PillTabs
            value={view}
            onChange={(next) => setView(next as 'list' | 'aggregates')}
            options={[
              { value: 'list', label: translate('pay.operations.tabs.list') },
              { value: 'aggregates', label: translate('pay.operations.tabs.aggregates') },
            ]}
          />
        }
        filters={
          <FiltersPopover
            activeCount={activeCount}
            onClear={() =>
              view === 'list'
                ? setOperationsFilters(DEFAULT_OPERATIONS_FILTERS)
                : setAggregatesFilters(DEFAULT_AGGREGATES_FILTERS)
            }
          >
            {view === 'list' ? (
              <OperationsFiltersForm filters={operationsFilters} onChange={updateOperationsFilters} />
            ) : (
              <AggregatesFiltersForm filters={aggregatesFilters} onChange={updateAggregatesFilters} />
            )}
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onExport={view === 'list' ? (exporting ? undefined : handleExport) : undefined}
      />
      {view === 'list' ? (
        <OperationsListView key={refreshToken} filters={operationsFilters} onFiltersChange={updateOperationsFilters} />
      ) : (
        <AggregatesView key={refreshToken} filters={aggregatesFilters} />
      )}
    </Stack>
  );
}
